import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  GetSnippetsRequest,
  SaveSnippetRequest,
  DeleteSnippetRequest,
  InsertSnippetsRequest,
} from '../models/snippet-requests.model';
import { SnippetsViewModel } from '../models/snippets-view.model';
import { ApiService } from './api.service';
import { take } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class SnippetService {
  private baseUrl = 'Snippet';
  private snippetsCache: BehaviorSubject<SnippetsViewModel[]> = new BehaviorSubject<SnippetsViewModel[]>([]); // Cache for snippets
  readonly snippets$ = this.snippetsCache.asObservable();
  private lastCacheTime: number | null = null; // Time the cache was last updated
  private cacheDuration = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(private api: ApiService) {}

  // Method to get snippets with cache logic
  getSnippets(request: GetSnippetsRequest, bypassCache: boolean = false): void {
    const currentTime = new Date().getTime();
    if (
      bypassCache == false &&
      this.snippetsCache &&
      this.lastCacheTime &&
      currentTime - this.lastCacheTime < this.cacheDuration
    ) {
      return;
    } else {
      // Fetch data from API and update cache
      this.api.post<SnippetsViewModel[]>(`${this.baseUrl}/GetSnippets`, request).pipe(
        tap((snippets: SnippetsViewModel[]) => {
          this.updateCache(snippets);
        })
      ).subscribe();
    }
  }

  // Method to save a snippet and update the cache with the returned list
  logSnippetUsage(request: InsertSnippetsRequest) {
    this.api.post<null>(`${this.baseUrl}/InsertSnippetIntoDocument`, request).subscribe();
  }

  // Method to save a snippet and update the cache with the returned list
  saveSnippet(request: SaveSnippetRequest): Observable<SnippetsViewModel[]> {
    return this.api.post<SnippetsViewModel[]>(`${this.baseUrl}/SaveSnippet`, request, { showToast: false }).pipe(
      tap((updatedSnippets: SnippetsViewModel[]) => {
        // Update cache with the new list returned from the API
        this.updateCache(updatedSnippets);
      })
    );
  }

  // Method to delete a snippet and update the cache with the returned list
  deleteSnippet(request: DeleteSnippetRequest): Observable<SnippetsViewModel[]> {
    return this.api.post<SnippetsViewModel[]>(`${this.baseUrl}/DeleteSnippet`, request).pipe(
      tap((updatedSnippets: SnippetsViewModel[]) => {
        // Update cache with the new list returned from the API
        this.updateCache(updatedSnippets);
      })
    );
  }

  // Helper method to update the cache and set the timestamp
  private updateCache(snippets: SnippetsViewModel[]) {
    this.snippetsCache.next(snippets);
    this.lastCacheTime = new Date().getTime();
  }
}
